// Analytics card: the estimate time series for one screen. Operationally
// this is a daily surface, so it gets real range controls: trailing presets
// plus a custom from/to window, all in one filter row above the chart. The
// offset rides in the same chart (it is minutes like everything else) as a
// step line, so "who changed the offset and when" is visible against the
// displayed value it shaped.

import { Card, Input, Select } from "@pej/kore/components";
import { useTimeFormatter } from "@pej/kore/intl";
import { useMemo, useState } from "react";
import { useEstimates } from "@/api/analytics";
import type { Screen } from "@/api/types";
import {
    type ChartOutage,
    EstimateChart,
    type EstimateChartPoint,
    outageBands,
} from "@/features/screens/estimate-chart";

const PRESETS = {
    "2h": { label: "Last 2 hours", ms: 2 * 60 * 60 * 1000 },
    "24h": { label: "Last 24 hours", ms: 24 * 60 * 60 * 1000 },
    "7d": { label: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000 },
} as const;

type PresetKey = keyof typeof PRESETS;
type RangeKey = PresetKey | "custom";

const RANGE_LABELS: Record<RangeKey, string> = {
    "2h": PRESETS["2h"].label,
    "24h": PRESETS["24h"].label,
    "7d": PRESETS["7d"].label,
    custom: "Custom range…",
};

const SERVER_ROW_LIMIT = 5000;

function isRangeKey(value: unknown): value is RangeKey {
    return value === "2h" || value === "24h" || value === "7d" || value === "custom";
}

/** datetime-local value ("2026-08-30T14:00") → ISO instant, or null. */
function localToIso(value: string): string | null {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export interface AnalyticsCardProps {
    screen: Screen;
}

export function AnalyticsCard({ screen }: AnalyticsCardProps) {
    const [rangeKey, setRangeKey] = useState<RangeKey>("24h");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    // Preset windows anchor once per selection so the query key stays
    // stable between renders instead of refetching on every paint.
    const presetRange = useMemo(() => {
        const key: PresetKey = rangeKey === "custom" ? "24h" : rangeKey;
        const to = new Date();
        const from = new Date(to.getTime() - PRESETS[key].ms);
        return { from: from.toISOString(), to: to.toISOString() };
    }, [rangeKey]);

    const customRange = useMemo(() => {
        const from = localToIso(customFrom);
        const to = localToIso(customTo);
        return from && to && from < to ? { from, to } : null;
    }, [customFrom, customTo]);

    // A custom selection only takes effect once both ends are valid; until
    // then the previous preset window keeps the chart populated.
    const range = rangeKey === "custom" && customRange ? customRange : presetRange;
    const estimates = useEstimates(screen.id, range);
    const formatTime = useTimeFormatter();

    const points: EstimateChartPoint[] = (estimates.data?.points ?? [])
        .map((p) => ({
            at: Date.parse(p.computedAt),
            displayed: p.displayedMinutes,
            raw: p.rawMinutes,
            minimum: p.minimumMinutes,
            offset: p.offsetMinutes,
            samples: p.sampleCount,
        }))
        .sort((a, b) => a.at - b.at);
    const latest = points[points.length - 1];
    const outages: ChartOutage[] = (estimates.data?.outages ?? []).map((outage) => ({
        from: Date.parse(outage.from),
        to: outage.to === null ? null : Date.parse(outage.to),
    }));
    const shownOutages = outageBands(points, outages);

    return (
        <Card className="p-6">
            <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-base font-semibold">Estimate history</h2>
                    <p className="text-sm text-muted-foreground">
                        Displayed vs raw vs minimum, with the offset in effect over time. All series in minutes.
                    </p>
                </div>
                <div className="flex items-end gap-2">
                    {rangeKey === "custom" && (
                        <>
                            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                                <span aria-hidden="true">From</span>
                                <Input
                                    type="datetime-local"
                                    aria-label="Range start"
                                    value={customFrom}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                />
                            </div>
                            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                                <span aria-hidden="true">To</span>
                                <Input
                                    type="datetime-local"
                                    aria-label="Range end"
                                    value={customTo}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                />
                            </div>
                        </>
                    )}
                    <Select
                        value={rangeKey}
                        onValueChange={(value) => {
                            if (isRangeKey(value)) setRangeKey(value);
                        }}
                    >
                        <Select.Trigger aria-label="Range">
                            <Select.Value>{(value: unknown) => RANGE_LABELS[value as RangeKey]}</Select.Value>
                        </Select.Trigger>
                        <Select.Content>
                            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
                                <Select.Item key={key} value={key}>
                                    {RANGE_LABELS[key]}
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select>
                </div>
            </div>
            {estimates.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : points.length === 0 ? (
                <p className="text-sm text-muted-foreground">No estimates in this range yet.</p>
            ) : (
                <>
                    <div data-testid="estimate-chart">
                        <EstimateChart
                            points={points}
                            outages={outages}
                            formatTime={(at) => formatTime(new Date(at))}
                        />
                    </div>
                    {shownOutages.length > 0 && (
                        <p className="mt-2 text-xs text-muted-foreground" data-testid="outage-note">
                            Shaded: the service was offline. Values there were estimated from orders already in hand, so
                            they age as the outage runs on.
                        </p>
                    )}
                    {latest && (
                        <p className="mt-2 text-xs text-muted-foreground" data-testid="latest-sample">
                            Latest point: {latest.raw !== null ? `${latest.raw} min from ` : "no estimate from "}
                            {latest.samples} sampled order{latest.samples === 1 ? "" : "s"}
                            {`, offset ${latest.offset} min.`}
                        </p>
                    )}
                    {points.length === SERVER_ROW_LIMIT && (
                        <p className="mt-2 text-xs text-muted-foreground">
                            Showing the newest {SERVER_ROW_LIMIT} points — the oldest part of this range is cut off.
                        </p>
                    )}
                </>
            )}
        </Card>
    );
}
